import { computed, onScopeDispose, ref } from 'vue'
import { defineStore } from 'pinia'
import type { AdminNotice, TaskRecord } from '@/types/admin'
import { adapter } from '@/services/prototype-adapter'

export const useDataStore = defineStore('data', () => {
  const version = ref(0)
  const ticker = ref<number | null>(null)
  const tickCount = ref(0)

  function markChanged(): void {
    version.value += 1
  }

  function reset(): void {
    adapter.reset()
    tickCount.value = 0
    markChanged()
  }

  function mutate<T>(operation: () => T): T {
    const result = operation()
    markChanged()
    return result
  }

  function startTicker(): void {
    if (ticker.value !== null || typeof window === 'undefined') return
    ticker.value = window.setInterval(() => {
      const running = adapter.table('async_tasks').some(task => ['queued', 'running'].includes(String(task.status)))
      if (!running) return
      adapter.advanceTasks()
      tickCount.value += 1
      markChanged()
    }, 2400)
  }

  function stopTicker(): void {
    if (ticker.value !== null) {
      window.clearInterval(ticker.value)
      ticker.value = null
    }
  }

  onScopeDispose(stopTicker)

  const tasks = computed<TaskRecord[]>(() => {
    void version.value
    return adapter.table('async_tasks').map(row => ({
      id: String(row.id),
      taskNo: String(row.task_no ?? row.taskNo ?? ''),
      taskType: String(row.task_type ?? ''),
      title: String(row.title ?? ''),
      status: row.status as TaskRecord['status'],
      progress: Number(row.progress ?? 0),
      total: Number(row.total ?? 0),
      success: Number(row.success ?? 0),
      failed: Number(row.failed ?? 0),
      message: String(row.message ?? ''),
      createdAt: String(row.created_at ?? ''),
      finishedAt: row.finished_at ? String(row.finished_at) : undefined,
      retryable: Boolean(row.retryable || row.failed),
    }))
  })

  const notices = computed<AdminNotice[]>(() => {
    void version.value
    return adapter.notices()
  })

  const unreadCount = computed(() => notices.value.filter(item => !item.read).length)
  const runningTasks = computed(() => tasks.value.filter(item => ['queued', 'running'].includes(item.status)).length)

  const settings = computed(() => {
    void version.value
    return { ...adapter.settings }
  })

  function markRead(id: string): void {
    adapter.markNoticeRead(id)
    markChanged()
  }

  function markAllRead(): void {
    adapter.markAllNoticesRead()
    markChanged()
  }

  function refreshTask(id: string): void {
    adapter.retryTask(id)
    markChanged()
  }

  return {
    version,
    tickCount,
    tasks,
    notices,
    unreadCount,
    runningTasks,
    settings,
    markChanged,
    reset,
    mutate,
    startTicker,
    stopTicker,
    markRead,
    markAllRead,
    refreshTask,
    adapter,
  }
})
